/**
 * Page-view beacon (replaces the old `fetch('/api/pv?channel_id=&video_id=')` on mount).
 *
 * Pages opt in with `<script type="application/json" id="rt-pageview">{"surface":"watch",…}</script>`.
 * The beacon is `POST /api/pv` with a JSON body sent as `text/plain;charset=UTF-8` (a string
 * body to `navigator.sendBeacon`, which keeps it a CORS-simple request); the server must
 * parse the body as JSON regardless of Content-Type. Falls back to `fetch(keepalive)`.
 * Skipped on localhost (old behaviour) unless the script has `data-force`, and deferred while
 * the page is prerendering.
 */
import {
  channelRefDecoder,
  videoRefDecoder,
  type ChannelRefString,
  type VideoRefString,
} from '../lib/ref';
import { decodeJson, literal, object, optional, type Infer } from '../lib/validate';

export const PAGEVIEW_ENDPOINT = '/api/pv';
export const PAGEVIEW_CONFIG_ID = 'rt-pageview';
export const MAX_REFERRER_LENGTH = 1024;

export const pageViewConfigDecoder = object({
  surface: literal('watch', 'embed', 'channel'),
  video: optional(videoRefDecoder),
  channel: optional(channelRefDecoder),
});

export type PageViewConfig = Infer<typeof pageViewConfigDecoder>;

/** Wire payload of `POST /api/pv` (see CONTRACT.md). */
export interface PageViewPayload {
  readonly v: 1;
  readonly surface: PageViewConfig['surface'];
  readonly video: VideoRefString | null;
  readonly channel: ChannelRefString | null;
  /** `document.referrer`, truncated; '' when none. The server keeps only the host. */
  readonly referrer: string;
}

export function buildPageViewPayload(config: PageViewConfig, referrer: string): PageViewPayload {
  return {
    v: 1,
    surface: config.surface,
    video: config.video ?? null,
    channel: config.channel ?? null,
    referrer: referrer.slice(0, MAX_REFERRER_LENGTH),
  };
}

const localHosts = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export function isLocalHost(hostname: string): boolean {
  return localHosts.has(hostname) || hostname.endsWith('.localhost');
}

export interface BeaconEnv {
  readonly sendBeacon?: ((url: string, data: string) => boolean) | undefined;
  readonly fetch?: typeof fetch | undefined;
}

export function sendPageView(payload: PageViewPayload, env: BeaconEnv): boolean {
  const body = JSON.stringify(payload);
  if (env.sendBeacon?.(PAGEVIEW_ENDPOINT, body)) return true;
  if (!env.fetch) return false;
  env
    .fetch(PAGEVIEW_ENDPOINT, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      credentials: 'same-origin',
    })
    .catch(() => undefined);
  return true;
}

/** Reads the page's beacon config and sends one page view. Returns the payload sent, if any. */
export function trackPageView(doc: Document = document): PageViewPayload | null {
  const script = doc.getElementById(PAGEVIEW_CONFIG_ID);
  if (!script) return null;
  const result = decodeJson(pageViewConfigDecoder, script.textContent);
  if (!result.ok) {
    console.error('[pageview] invalid config:', result.error);
    return null;
  }
  // `data-force` lets developers exercise the endpoint locally.
  if (isLocalHost(doc.location.hostname) && !script.hasAttribute('data-force')) return null;
  const payload = buildPageViewPayload(result.value, doc.referrer);
  const send = (): void => {
    sendPageView(payload, {
      sendBeacon:
        'sendBeacon' in navigator ? (url, data) => navigator.sendBeacon(url, data) : undefined,
      fetch: window.fetch.bind(window),
    });
  };
  const prerendering = (doc as Document & { prerendering?: boolean }).prerendering === true;
  if (prerendering) doc.addEventListener('prerenderingchange', send, { once: true });
  else send();
  return payload;
}
